// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import {
  useState,
  useEffect,
  useContext,
  useRef,
  SyntheticEvent,
  useMemo,
} from 'react';
import { useRouter } from 'next/router';

import { EventListItem } from 'presentations/api/schema/event';
import {
  adminUserListPath,
  apiPath,
  dashboardCalendarsPath,
  dashboardEventEditPath,
  dashboardPath,
  eventEditPath,
  webPath,
} from 'presentations/routes/path';
import {
  defaultCacheExpiration,
  getPersistentItemWithViewer,
  setPersistentItemWithViewer,
} from 'presentations/views/helpers/storage';

import { patch } from 'infras/rest_client';
import {
  DateTime,
  formatToSeconds,
  formatToYMD,
  getDateTime,
} from 'infras/datetime';

import { isEventListItem } from 'presentations/views/helpers/event';
import { Status } from 'domains/models/event/status';
import { DateControllerActionType } from 'presentations/views/components/molecules/DateController';
import { UserUpdateBody, ViewerItem } from 'presentations/api/schema/user';
import { useRequest } from 'presentations/views/hooks/request';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import {
  DashboardPortalContext,
  setErrorMessage,
  setSuccessMessage,
} from 'presentations/reducers/DashboardPortal';
import { ProjectEventListItem } from 'presentations/api/schema/project/project_event';
import { selectableUserUidsKey } from 'presentations/views/components/organisms/DashboardEventCreateCheckList/hooks';
import { UserAppItem } from 'presentations/api/schema/account';
import { CalendarEventListItem } from 'presentations/api/schema/calendar_event';
import { CalendarSourceType } from 'presentations/api/schema/calendar_event/enum';
import { IntegrationMergedEventListItem } from 'presentations/api/schema/calendar_event/integrationMerged';
import { moveFromOrDefaultPath } from 'presentations/views/helpers/router';
import { isMediaMax, isMediaMaxWidth } from 'presentations/views/styles';
import { EventTypeUserListItem } from 'presentations/api/schema/event_type_user';
import { SortType } from 'domains/models/team_user_order/sortType';
import {
  Behavior,
  CalendarSource,
  MergeableEventListItem,
  Schedule,
  ScheduleType,
  ShowRange,
  CalendarWeekDay,
} from '../../atoms/Calendar/reducer';
import {
  CalendarContext,
  scrollToPage,
  setContainerWidth,
  setCreatingEvent,
  setIsShowSameEventMerge,
  setIsShowEventOverlap,
  setSelectedGroups,
  setSelectedSlots,
  setShowRange,
  setStartWeekday,
  setUserColor,
  setRulerTimeZones,
  setIsShowDeclinedEvent,
  setSchedules,
} from '../../atoms/Calendar/reducer/CalendarContext';
import {
  getCurrentStartedAt,
  getNow,
} from '../../atoms/Calendar/reducer/useCalendarProvider';
import { calendarNewEventPrefix } from '../../atoms/Calendar/DayTimeGrid/Event';
import { getErrorMessage } from 'presentations/views/helpers/error';
import { ResponseStatus } from 'domains/models/event/responseStatus';
import { flexibleStartWeekDay } from 'domains/models/event_type/startWeekDay';
import { showRangeForCalendarKey } from 'presentations/views/helpers/storage/config';
import { ProviderType } from 'domains/models/calendar/providerType';

export const selectedUserIdsForCalendarKey = 'selectedUserIdsForCalendar';

interface Props {
  viewer: ViewerItem;
  startWeekday: CalendarWeekDay;
  // viewType: EventCreateViewType;
}

interface ShowingEventDetail {
  mergedEvent: IntegrationMergedEventListItem;
  userId: string;
  element: HTMLElement;
}

export const useDashboardCalendarPage = (props: Props) => {
  const t = translateInComponent(translations);
  const { state, dispatch } = useContext(CalendarContext);
  const portalContext = useContext(DashboardPortalContext);

  const [creatingElement, setCreatingElement] = useState<HTMLElement | null>(
    null,
  );
  const [isOpenUserList, setIsOpenUserList] = useState<boolean>(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [
    confirmResponseStatus,
    setConfirmResponseStatus,
  ] = useState<ResponseStatus | null>(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);

  const router = useRouter();

  // const [viewType, setViewType] = useState<EventCreateViewType>(
  //   EventCreateViewType.Calendar,
  // );
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [isNeedsRefresh, setIsNeedRefresh] = useState<boolean>(false);
  const [dataStartedAt, setDataStartedAt] = useState<DateTime | null>(null);
  const [dataEndedAt, setDataEndedAt] = useState<DateTime | null>(null);

  const [startWeekdaySetting, setStartWeekdaySetting] = useState<
    CalendarWeekDay
  >(props.startWeekday);

  const [selectedEvent, setSelectedEvent] = useState<ShowingEventDetail | null>(
    null,
  );

  const [settingAnchor, setSettingAnchor] = useState<null | HTMLElement>(null);
  const [timeZoneAnchor, setTimeZoneAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [
    timeZoneMenuAnchor,
    setTimeZoneMenuAnchor,
  ] = useState<null | HTMLElement>(null);
  const [selectedTimeZone, setSelectedTimeZone] = useState<null | string>(null);

  const isMobile = isMediaMax('tablet');

  const onClickSetting = (e: SyntheticEvent<HTMLInputElement>) => {
    e.preventDefault();
    setSettingAnchor(settingAnchor ? null : e.currentTarget);
  };
  const onCloseSetting = () => {
    setSettingAnchor(null);
  };

  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const isEnableShowSub = !isMediaMaxWidth(960);
  const isTablet = isMediaMax('tablet');
  const isShowSub = isEnableShowSub && !!(state.creatingEvent || selectedEvent);

  const startedAt = state.pages[state.currentPage];

  useEffect(() => {
    setIsNeedRefresh(true);
    if (state.showRange.month || state.showRange.day) {
      dispatch(scrollToPage(1));
    }
  }, [state.showRange]);

  useEffect(() => {
    if (!router.query.start || !router.query.end) return;
    dispatch(
      setCreatingEvent(
        setDefaultCreateEvent(
          getDateTime(Number(router.query.start), state.timeZone),
          getDateTime(Number(router.query.end), state.timeZone),
          false,
        ),
      ),
    );
    // TODO creatingEventを作る
  }, [router.query.start, router.query.end]);

  const onBackFromDay = (e: any) => {
    const url = new URL(window.location.href);
    const month = url.searchParams.get('month');
    if (month) {
      dispatch(setShowRange({ month: 1 }, getDateTime(month, state.timeZone)));
    }
  };

  useEffect(() => {
    window.addEventListener('popstate', onBackFromDay);
    return () => window.removeEventListener('popstate', onBackFromDay);
  }, [state.showRange.month]);

  useEffect(() => {
    if (!state.showRange.month) return;
    history.replaceState(
      null,
      '',
      dashboardCalendarsPath({
        month: formatToYMD(startedAt.startOf('month')),
      }),
    );
  }, [startedAt]);

  // MonthとDayの行き来
  const onClickCalendarMoreEvent = async (
    date: DateTime,
    element: HTMLElement,
  ) => {
    router.push(dashboardCalendarsPath({ day: formatToYMD(date) }), undefined);
  };

  const onClickCalendarDay = async (date: DateTime, element: HTMLElement) => {
    router.push(dashboardCalendarsPath({ day: formatToYMD(date) }), undefined);
  };

  // 前回の表示タイプと週or月にする
  useEffect(() => {
    let selectableUserUids = getPersistentItemWithViewer(
      selectableUserUidsKey,
      props.viewer,
    );
    if (selectableUserUids && selectableUserUids.length > 0) {
      // viewerを先頭に固定
      if (selectableUserUids.includes(props.viewer.uid)) {
        selectableUserUids = selectableUserUids.filter(
          (uid: string) => uid !== props.viewer.uid,
        );
      }
      selectableUserUids = [props.viewer.uid, ...selectableUserUids];

      setSelectableUserUids(selectableUserUids);
    } else {
      setSelectableUserUids([props.viewer.uid]);
    }
    let selectedUserIds = getPersistentItemWithViewer(
      selectedUserIdsForCalendarKey,
      props.viewer,
    );
    if (selectedUserIds && selectedUserIds.length > 0) {
      // viewerを先頭に固定
      if (selectedUserIds.includes(props.viewer.uid)) {
        selectedUserIds = selectedUserIds.filter(
          (uid: string) => uid !== props.viewer.uid,
        );
      }
      selectedUserIds = [props.viewer.uid, ...selectedUserIds];

      setSelectedUserIds(selectedUserIds);
    } else {
      setSelectedUserIds([props.viewer.uid]);
    }
    setInitialized(true);
  }, []);
  useEffect(() => {
    if (!state.isReady) return;
    if (!state.showRange.day && !state.showRange.month) return;
    if (isNeedsRefresh || !dataStartedAt || !dataEndedAt) {
      // 初回
      setDataStartedAt(startedAt.minus(state.showRange));
      setDataEndedAt(
        startedAt.plus(
          state.showRange.day
            ? { day: state.showRange.day * 3 }
            : { month: state.showRange.month! * 3 },
        ),
      );
      setIsNeedRefresh(false);
    } else if (startedAt.toMillis() === dataEndedAt.toMillis()) {
      setDataStartedAt(dataEndedAt);
      setDataEndedAt(
        dataEndedAt.plus(
          state.showRange.day
            ? { day: state.showRange.day * 4 }
            : { month: state.showRange.month! * 4 },
        ),
      );
    } else if (startedAt.toMillis() < dataStartedAt.toMillis()) {
      setDataEndedAt(dataStartedAt);
      setDataStartedAt(
        dataStartedAt.minus(
          state.showRange.day
            ? { day: state.showRange.day * 4 }
            : { month: state.showRange.month! * 4 },
        ),
      );
    }
  }, [
    state.isReady,
    isNeedsRefresh,
    startedAt,
    state.showRange,
    dataStartedAt,
    dataEndedAt,
  ]);

  useEffect(() => {
    if (!state.containerRef?.current) return;
    const el: any = state.containerRef.current;
    if (!el?.clientWidth) return;
    dispatch(setContainerWidth(`${el.clientWidth}px`));
  }, [isShowSub, state.containerRef]);

  const cacheExpSeconds = useMemo(() => {
    if (!dataStartedAt || !dataEndedAt) return undefined;
    if (dataEndedAt.toMillis() < getDateTime().toMillis()) {
      // 過去は1日キャッシュ
      return 60 * 60 * 24;
    }
    return 60 * 60 * 1;
  }, [dataStartedAt, dataEndedAt]);

  const eventsRequest = useRequest(
    selectedUserIds.length > 0 &&
      !isNeedsRefresh &&
      dataEndedAt &&
      dataStartedAt
      ? apiPath(`/v1/teams/${props.viewer.team!.uid}/users/schedules`, {
          started: dataStartedAt.toMillis(),
          ended: dataEndedAt.toMillis(),
          userId: selectedUserIds,
          seminarFilter: 'grouping',
          isIncludeCalendar: 1,
          isIncludeDeclined: state.isShowDeclinedEvent ? 1 : 0,
          includeReservedEvent: 1,
          isMergeSameIntegratedEvent: 0,
        })
      : null,
    { cacheExpSeconds },
  );
  const isLoading = selectedUserIds.length > 0 && eventsRequest.isLoading;
  const events: ProjectEventListItem[] = eventsRequest.data || [];

  const [
    isEventDateCreationSpecified,
    setIsEventDateCreationSpecified,
  ] = useState<boolean>(false);

  const [selectableUserUids, setSelectableUserUids] = useState<string[]>([
    props.viewer.uid,
  ]);

  // カレンダー表示設定をしているTeamUserの一覧取得
  const teamUsersRequest = useRequest(
    apiPath(`/v1/teams/${props.viewer.team!.uid}/users`, {
      page: 1,
      perPage: 100,
      sortType: SortType.Calendar,
    }),
  );
  const allTeamUsers: TeamUserListItem[] = teamUsersRequest.data || [];
  const viewerTeamUser = useMemo(() => {
    return allTeamUsers.find(
      (user: TeamUserListItem) => user.userId === props.viewer.uid,
    );
  }, [props.viewer, allTeamUsers]);

  const selectableTeamUsers = useMemo(() => {
    const users = (teamUsersRequest.data ?? []).filter(
      (u: TeamUserListItem) =>
        selectableUserUids.includes(u.userId) && u.userId !== props.viewer.uid,
    );
    // viewerを先頭に固定
    if (viewerTeamUser) users.unshift(viewerTeamUser);
    return users;
  }, [props.viewer, allTeamUsers, selectableUserUids, viewerTeamUser]);

  const selectedTeamUsers = useMemo(() => {
    return allTeamUsers.filter((u) => selectedUserIds.includes(u.userId));
  }, [props.viewer, allTeamUsers, selectedUserIds]);

  const selectedEventId = useMemo(() => {
    if (!selectedEvent) return null;
    if (
      selectedEvent.mergedEvent.calendarSourceType === CalendarSourceType.Jicoo
    ) {
      return selectedEvent.mergedEvent.event.uid;
    } else {
      const e: any = selectedEvent.mergedEvent.event;
      return [
        e.calendarId || selectedEvent.mergedEvent.calendar.id,
        e.uid,
      ].join('-');
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (!viewerTeamUser) return;
    dispatch(setUserColor(viewerTeamUser.color));
  }, [viewerTeamUser]);

  useEffect(() => {
    if (isEventDateCreationSpecified) return;
    if (!router.isReady || !initialized || teamUsersRequest.isLoading) return;
    if (!router.query.start || !router.query.end) return;

    // const isSingleUserView =
    //   viewType === EventCreateViewType.Task ||
    //   (viewType === EventCreateViewType.Block && state.showRange.day);
    const targetUserUid =
      typeof router.query.eventUser === 'string'
        ? router.query.eventUser
        : props.viewer.uid;

    // ユーザが指定されているか、単一ユーザの予定作成を行うビューの場合は選択ユーザに追加する
    if (typeof router.query.eventUser === 'string') {
      // if (typeof router.query.eventUser === 'string' || isSingleUserView) {
      if (!selectableUserUids.includes(targetUserUid)) {
        const newUids = [...selectableUserUids, targetUserUid];

        const sortedUids: string[] = [];
        allTeamUsers.forEach((teamUser) => {
          if (newUids.includes(teamUser.userId)) {
            sortedUids.push(teamUser.userId);
          }
        });

        setSelectableUserUids(sortedUids);
        setPersistentItemWithViewer(
          selectableUserUidsKey,
          sortedUids,
          defaultCacheExpiration,
          props.viewer,
        );
      }
      if (!selectedUserIds.includes(targetUserUid)) {
        const newUids = [...selectedUserIds, targetUserUid];

        const sortedUids: string[] = [];
        allTeamUsers.forEach((teamUser) => {
          if (newUids.includes(teamUser.userId)) {
            sortedUids.push(teamUser.userId);
          }
        });

        setSelectedUserIds(sortedUids);
        setPersistentItemWithViewer(
          selectedUserIdsForCalendarKey,
          sortedUids,
          defaultCacheExpiration,
          props.viewer,
        );
      }
    }

    setIsEventDateCreationSpecified(true);
  }, [router.isReady, initialized, teamUsersRequest.isLoading]);

  const userAppRequest = useRequest(apiPath(`/v1/accounts/apps`));
  const userApp: UserAppItem | null = userAppRequest.data;

  // イベント
  const onCancelledEvent = (event: EventListItem) => {
    const newEvents = events.map((userEvent) => {
      return {
        userId: userEvent.userId,
        events: userEvent.events.map((e) => {
          if (isEventListItem(e.event) && e.event.id === event.id) {
            e.event.status = Status.Cancel;
          }

          return e;
        }),
      };
    });
    eventsRequest.mutate({
      ...eventsRequest,
      data: newEvents,
    });
  };

  const onChangeTerm = (action?: DateControllerActionType) => {
    let currentPage = state.currentPage;
    if (action) {
      switch (action) {
        case 'prev': {
          currentPage--;
          break;
        }
        case 'today': {
          const startedAt = getCurrentStartedAt({
            current: getNow(state.timeZone),
            startWeekday: state.startWeekday,
            showRange: state.showRange,
          });

          if (state.showRange.day || state.showRange.month) {
            setDataStartedAt(startedAt.minus(state.showRange));
            setDataEndedAt(
              startedAt.plus(
                state.showRange.day
                  ? { day: state.showRange.day * 3 }
                  : { day: state.showRange.month! * 3 },
              ),
            );
          }

          for (let index = 0; index < state.pages.length; index++) {
            const page = state.pages[index];
            if (page.toMillis() === startedAt.toMillis()) {
              currentPage = index;
              break;
            }
          }
          break;
        }
        case 'next': {
          currentPage++;
          break;
        }
      }
    }
    dispatch(scrollToPage(currentPage));
  };

  const onCreateEventSuccess = async (
    newEvent: EventListItem,
    selectedUsers: (string | EventTypeUserListItem)[],
  ) => {
    if (isEventDateCreationSpecified) {
      setIsEventDateCreationSpecified(false);
      setCreatingElement(null);
      onCloseEventCreate();
      moveFromOrDefaultPath(router, dashboardPath());
    } else {
      if (!state.schedules) return;
      if (!state.creatingEvent) return;
      onCloseEventCreate();
      dispatch(
        setSchedules([
          ...state.schedules,
          { ...state.creatingEvent, uid: 'new-loading-event' },
        ]),
      );
      await eventsRequest.mutate();
    }
  };

  const onClickInvite = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(webPath(adminUserListPath(), router.locale));
  };

  const onClickCalendarEvent = async (
    event: MergeableEventListItem,
    element: HTMLElement,
  ) => {
    for (const userEvent of events) {
      let mergedEvent;
      if (event.isRecurringEvent && event.providerType === ProviderType.Jicoo) {
        mergedEvent =
          userEvent.events.find(
            (e: any) =>
              e.event.uid === event.eventId &&
              formatToSeconds(event.startedAt) ===
                formatToSeconds(getDateTime(e.event.startedAt)),
          ) || null;
      } else {
        mergedEvent =
          userEvent.events.find(
            (e: any) =>
              e.event.uid === event.eventId &&
              (event.calendarId
                ? e.event.calendarId === event.calendarId
                : true),
          ) || null;
      }
      if (mergedEvent) {
        setSelectedEvent({ mergedEvent, userId: event.userId, element });
        break;
      }
    }
  };

  const onClickCloseEvent = () => {
    setSelectedEvent(null);
  };

  const onClickFilter = () => {
    setIsMobileFilterOpen(true);
  };

  const onCloseFilter = (e?: SyntheticEvent) => {
    e && e.preventDefault();

    setIsMobileFilterOpen(false);
  };

  const onChangeUserSort = async (sortedUsers: TeamUserListItem[]) => {
    teamUsersRequest.mutate({
      ...teamUsersRequest,
      data: sortedUsers,
    });

    const sortedUids: string[] = [];
    sortedUsers.forEach((teamUser) => {
      if (selectedUserIds.includes(teamUser.userId)) {
        sortedUids.push(teamUser.userId);
      }
    });
    setSelectedUserIds(sortedUids);
    setPersistentItemWithViewer(
      selectedUserIdsForCalendarKey,
      sortedUids,
      defaultCacheExpiration,
      props.viewer,
    );
  };

  const onChangeUserAdd = async (newUser: TeamUserListItem) => {
    teamUsersRequest.revalidate();

    const newSelectedUids: string[] = [...selectedUserIds, newUser.userId];
    setSelectedUserIds(newSelectedUids);
    setPersistentItemWithViewer(
      selectedUserIdsForCalendarKey,
      newSelectedUids,
      defaultCacheExpiration,
      props.viewer,
    );

    const newSelectableUids: string[] = [...selectableUserUids, newUser.userId];
    setSelectableUserUids(newSelectableUids);
    setPersistentItemWithViewer(
      selectableUserUidsKey,
      newSelectableUids,
      defaultCacheExpiration,
      props.viewer,
    );
  };

  const onChangeUserDelete = (newUids: string[], targetUids: string[]) => {
    setSelectableUserUids(newUids);

    const newSelectedUids = selectedUserIds.filter((u) => newUids.includes(u));

    const addingTargetUids = targetUids.filter((targetUid) =>
      newUids.includes(targetUid),
    );
    addingTargetUids.forEach((addingTargetUid) => {
      if (!newSelectedUids.includes(addingTargetUid)) {
        newSelectedUids.push(addingTargetUid);
      }
    });

    const sortedUids: string[] = [];
    allTeamUsers.forEach((teamUser) => {
      if (newSelectedUids.includes(teamUser.userId)) {
        sortedUids.push(teamUser.userId);
      }
    });

    setSelectedUserIds(sortedUids);
    setPersistentItemWithViewer(
      selectedUserIdsForCalendarKey,
      sortedUids,
      defaultCacheExpiration,
      props.viewer,
    );
  };

  const onSelectUser = (user: TeamUserListItem) => {
    let newUids: string[];
    if (selectedUserIds.some((u) => u === user.userId)) {
      newUids = selectedUserIds.filter((u) => u !== user.userId);
    } else {
      newUids = [...selectedUserIds, user.userId];
    }

    const sortedUids: string[] = [];
    allTeamUsers.forEach((teamUser) => {
      if (newUids.includes(teamUser.userId)) {
        sortedUids.push(teamUser.userId);
      }
    });

    setSelectedUserIds(sortedUids);
    setSelectedEvent(null);
    setPersistentItemWithViewer(
      selectedUserIdsForCalendarKey,
      sortedUids,
      defaultCacheExpiration,
      props.viewer,
    );
  };

  const onChangeView = async (view: number) => {
    let range: ShowRange = { day: 7 };
    switch (view) {
      case 1: {
        range = { day: 1 };
        setSettingAnchor(null);
        break;
      }
      case 28: {
        range = { month: 1 };
        setSettingAnchor(null);
        break;
      }
      case 7:
        range = { day: view };
        setSettingAnchor(null);
        break;
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
        range = { day: view };
        break;
      default:
        return;
    }
    if (isMobile) {
      setPersistentItemWithViewer(
        showRangeForCalendarKey,
        view,
        defaultCacheExpiration,
        props.viewer,
      );
    } else {
      await onUpdateUserSetting({ calendarViewDays: view });
    }
    history.replaceState(null, '', dashboardCalendarsPath());
    dispatch(setShowRange(range));
  };

  const onRescheduleEvent = () => {
    if (!selectedEvent) return;
    window.open(eventEditPath(selectedEvent.mergedEvent.event.uid));
  };

  const onEditEvent = () => {
    if (!selectedEvent) return;
    const calendarSourceType = selectedEvent.mergedEvent.calendarSourceType;
    switch (calendarSourceType) {
      case CalendarSourceType.Jicoo:
        router.push(
          dashboardEventEditPath(selectedEvent.mergedEvent.event.uid, {
            from: encodeURIComponent(router.asPath),
          }),
        );
        break;
      case CalendarSourceType.Integration:
        // if (detailEvent && detailEvent.htmlLink) {
        //   window.open(detailEvent.htmlLink);
        // }
        break;
      default:
        break;
    }
  };

  const onCloseEventCreate = () => {
    dispatch(setSelectedSlots([]));
    dispatch(setSelectedGroups([]));
    dispatch(setCreatingEvent(null));
    setCreatingElement(null);
  };

  const onCloseEventCreateForWithoutCalendar = (e: SyntheticEvent) => {
    // カレンダー内では閉じない
    const targetElement = e.target as HTMLElement;
    if (calendarContainerRef.current?.contains(targetElement)) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onCloseEventCreate();
  };

  const onChangeResponseStatus = async (
    newStatus: ResponseStatus,
    isRecurring?: boolean,
  ) => {
    if (!selectedEvent) return;

    const integrationEvent = {
      ...selectedEvent.mergedEvent.event,
    } as CalendarEventListItem;

    if (integrationEvent.isRecurringEvent && !confirmResponseStatus) {
      setConfirmResponseStatus(newStatus);
      return;
    }

    const data = { responseStatus: newStatus };
    setIsConfirmLoading(true);
    const result = await patch(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/events/${
          integrationEvent.uid
        }/patch`,
        {
          calendarId: integrationEvent.calendarId,
          isRecurring: isRecurring,
        },
      ),
      { data },
    );
    setIsConfirmLoading(false);
    if (!result.isSuccess) {
      // statusを戻す
      portalContext.dispatch(
        setErrorMessage(t('Response status update error')),
      );
      return;
    }
    const statusText = `Response Status ${newStatus}`;
    portalContext.dispatch(
      setSuccessMessage(
        t('Response status update success', { status: t(statusText) }),
      ),
    );
    setSelectedEvent(null);

    const newEvents = events.map((userEvent) => {
      return {
        userId: userEvent.userId,
        events: userEvent.events.map((e) => {
          if (
            e.calendarSourceType === CalendarSourceType.Integration &&
            e.event.uid === selectedEvent.mergedEvent.event.uid
          ) {
            const calendarEvent = e.event as CalendarEventListItem;
            for (let i = 0; i < calendarEvent.attendees.length; i++) {
              if (calendarEvent.attendees[i].email === viewerTeamUser?.email) {
                calendarEvent.attendees[i].responseStatus = newStatus;
                break;
              }
            }
            e.event = calendarEvent;
          }
          return e;
        }),
      };
    });
    eventsRequest.mutate({
      ...eventsRequest,
      data: newEvents,
    });
  };

  const setDefaultCreateEvent = (
    startedAt: DateTime,
    endedAt: DateTime,
    isAllDay: boolean,
  ) => {
    const schedule: Schedule = {
      scheduleType: ScheduleType.Event,
      uid: 'new-event',
      userId: props.viewer.uid,
      calendarId: '',
      eventId: 'new-event',
      startedAt: startedAt.toISO(),
      endedAt: endedAt.toISO(),
      color: state.userColor,
      name: t('New Event'),
      // x: newEvent.x,
      // y: newEvent.y,
      // ySpan: newEvent.ySpan,
      isAllDay,
      responseStatus: ResponseStatus.Accepted,
      timeZone: state.timeZone,
      providerType: ProviderType.Jicoo,
      url: null,
      status: Status.Open,
      isRecurringEvent: false,
      description: null,
      calendarSource: CalendarSource.Jicoo,
      address: null,
      behavior: Behavior.Event,
    };
    return schedule;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const el = document.getElementsByClassName(
        `${calendarNewEventPrefix}`,
      )[0];
      if (el) {
        setCreatingElement(el as HTMLElement);
      }
    }, 300);
    return function cleanUp() {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const newEvent = state.selectedGroups[0];
    if (!newEvent) return;
    dispatch(
      setCreatingEvent(
        setDefaultCreateEvent(
          newEvent.startedAt,
          newEvent.endedAt,
          newEvent.isAllDay,
        ),
      ),
    );
  }, [state.selectedGroups[0], isTablet]);

  const onUpdateSchedule = async (schedule: Schedule) => {
    const data = {
      startTime: schedule.startedAt,
      endTime: schedule.endedAt,
      isAllDay: schedule.isAllDay,
    };
    const result = await patch(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/events/${schedule.eventId}/patch`,
      ),
      { data },
    );
    if (!result.isSuccess) {
      portalContext.dispatch(setErrorMessage(getErrorMessage(result)));
      return;
    }
  };

  const onUpdateUserSetting = async (data: UserUpdateBody) => {
    const result = await patch(apiPath('/v1/user/update'), { data });
    if (!result.isSuccess) {
      portalContext.dispatch(setErrorMessage(t('failed to update')));
      return;
    }
  };

  const onClickOpenUserList = () => {
    setIsOpenUserList(!isOpenUserList);
  };

  const onChangeIsShowEventOverlap = async (value: boolean) => {
    dispatch(setIsShowEventOverlap(value));
    await onUpdateUserSetting({ isShowEventOverlap: value });
  };
  const onChangeIsShowSameEventMerge = async (value: boolean) => {
    dispatch(setIsShowSameEventMerge(value));
    await onUpdateUserSetting({ isShowSameEventMerge: value });
  };
  const onChangeStartWeekday = async (startWeekday: CalendarWeekDay) => {
    const wday = Number(startWeekday);
    if (wday === flexibleStartWeekDay) {
      dispatch(setStartWeekday((getDateTime().weekday % 7) as CalendarWeekDay));
    } else {
      dispatch(setStartWeekday(startWeekday));
    }
    setStartWeekdaySetting(startWeekday);
    await onUpdateUserSetting({ startWeekDay: wday });
  };
  const onChangeIsShowDeclinedEvent = async (value: boolean) => {
    dispatch(setIsShowDeclinedEvent(value));
    await onUpdateUserSetting({ isShowDeclinedEvent: value });
    eventsRequest.mutate();
  };

  const onClickAddTimeZone = (el: HTMLElement) => {
    setTimeZoneAnchor(el);
  };

  const onClickTimeZone = (timeZone: string, el: HTMLElement) => {
    setSelectedTimeZone(timeZone);
    setTimeZoneMenuAnchor(el);
  };

  const onSelectNewTimeZone = async (name: string) => {
    onCloseAddTimeZone();
    const extraTimeZones = [...state.rulerTimeZones, name];
    dispatch(setRulerTimeZones(extraTimeZones));
    await onUpdateUserSetting({ extraTimeZones });
  };
  const onCloseAddTimeZone = () => {
    setTimeZoneAnchor(null);
  };

  const onCloseTimeZoneMenu = () => {
    setTimeZoneMenuAnchor(null);
    setSelectedTimeZone(null);
  };

  const onDeleteTimeZoneMenu = async () => {
    if (!selectedTimeZone) return;
    setTimeZoneMenuAnchor(null);
    setSelectedTimeZone(null);
    const extraTimeZones = [...state.rulerTimeZones];
    const index = extraTimeZones.indexOf(selectedTimeZone);
    if (index === -1) return;
    extraTimeZones.splice(index, 1);
    dispatch(setRulerTimeZones(extraTimeZones));
    await onUpdateUserSetting({ extraTimeZones });
  };

  const onSubmitConfirm = async () => {
    if (confirmResponseStatus !== null) {
      setConfirmResponseStatus(null);
      onChangeResponseStatus(confirmResponseStatus);
    }
  };

  const onSubmitRecurringConfirm = async () => {
    if (confirmResponseStatus !== null) {
      setConfirmResponseStatus(null);
      onChangeResponseStatus(confirmResponseStatus, true);
    }
  };

  const onCloseConfirm = async () => {
    setConfirmResponseStatus(null);
  };

  return {
    state: {
      isLoading: isLoading,
      isMobileFilterOpen,
      isEventDateCreationSpecified,
      events,
      isLoadingTeamUsers: !teamUsersRequest.data,
      viewerTeamUser,
      selectableTeamUsers,
      selectedTeamUsers,
      selectableUserUids,
      selectedUserIds,
      projects: portalContext.state.projects,
      calendarContainerRef,
      // viewType,
      selectedEvent,
      userApp,
      settingAnchor,
      isOpenFilter: isMobileFilterOpen,
      creatingEvent: state.creatingEvent,
      creatingElement,
      isShowSub,
      isOpenUserList,
      showRange: state.showRange,
      // duration: state.duration,
      // interval: state.interval,
      isShowSameEventMerge: state.isShowSameEventMerge,
      isShowEventOverlap: state.isShowEventOverlap,
      isShowWeekends: true,
      isShowDeclinedEvent: state.isShowDeclinedEvent,
      timeFormat: state.timeFormat,
      startWeekday: state.startWeekday,
      timeZoneAnchor,
      isMobile,
      timeZone: state.timeZone,
      timeZoneMenuAnchor,
      selectedTimeZone,
      confirmResponseStatus,
      isConfirmLoading,
      selectedEventId,
      startWeekdaySetting,
    },
    actions: {
      onChangeTerm,
      onClickInvite,
      onClickCalendarEvent,
      onClickCalendarMoreEvent,
      onClickCalendarDay:
        isTablet && state.showRange.month ? onClickCalendarDay : undefined,
      // onClickEvent,
      onClickCloseEvent,
      onCloseFilter,
      onChangeUserSort,
      onChangeUserAdd,
      onChangeUserDelete,
      onSelectUser,
      // onChangeInterval,
      // onChangeDuration,
      // onChangeViewType,
      onChangeView,
      onChangeIsShowEventOverlap,
      onChangeIsShowSameEventMerge,
      onChangeIsShowDeclinedEvent,
      onChangeStartWeekday,
      onCancelledEvent,
      onEditEvent,
      onCreateEventSuccess,
      onCloseEventCreate,
      onCloseEventCreateForWithoutCalendar,
      onClickSetting,
      onCloseSetting,
      onClickFilter,
      onChangeResponseStatus,
      // onClickAddEvent,
      onClickOpenUserList,
      onUpdateSchedule,
      onClickAddTimeZone,
      onClickTimeZone,
      onCloseAddTimeZone,
      onSelectNewTimeZone,
      onCloseTimeZoneMenu,
      onDeleteTimeZoneMenu,
      onSubmitConfirm,
      onSubmitRecurringConfirm,
      onCloseConfirm,
    },
  };
};
